import React, { useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Typography,
  Box,
  CircularProgress,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import { useTranslation } from 'react-i18next';

// Kasa arayüzü
interface CashboxItem {
  id: string;
  name: string;
  description: string;
  currency: string;
  balance: number;
  createdAt: string;
  isActive: boolean;
  branchId?: string | null;
  companyBranchName?: string | null;
  isDefault?: boolean;
  hasTransactions?: boolean;
}

interface CashboxListWidgetProps {
  cashboxes: CashboxItem[];
  loading: boolean;
  getCurrencySymbol: (currency: string) => string;
  branches?: any[];
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 8,
  overflow: 'hidden',
  '& .MuiCardHeader-root': {
    padding: theme.spacing(1),
    '& .MuiTypography-h6': {
      fontSize: '1rem',
      fontWeight: 'bold',
    },
  },
  '& .MuiCardContent-root': {
    padding: theme.spacing(1),
  },
}));

// Şube bazlı gruplama tipi
interface BranchGroup {
  branchId: string | null;
  branchName: string;
  totalBalance: number;
  count: number;
  currency: string;
  items: CashboxItem[]; // Gruptaki bireysel kasalar (detay modalı için)
}

const CashboxListWidget: React.FC<CashboxListWidgetProps> = ({
  cashboxes,
  loading,
  getCurrencySymbol,
  branches = []
}) => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'tr-TR';

  // Seçilen şube grubu (detay modalı)
  const [selectedGroup, setSelectedGroup] = useState<BranchGroup | null>(null);

  const formatBalance = (value: number, currency: string) =>
    `${value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${getCurrencySymbol(currency)}`;

  // Kasaları şube bazlı grupla (şubesiz olanlar kendi adlarıyla listelenir)
  const branchGroups: BranchGroup[] = useMemo(() => {
    const groupMap = new Map<string, BranchGroup>();
    const noBranchItems: BranchGroup[] = [];

    cashboxes.forEach(cashbox => {
      const key = cashbox.branchId || null;

      if (!key) {
        // Şubeye bağlı değil → kendi adıyla ayrı satır olarak göster
        noBranchItems.push({
          branchId: null,
          branchName: cashbox.name || t('dashboard.cashboxesWidgetDefaultName'),
          totalBalance: cashbox.balance,
          count: 1,
          currency: cashbox.currency || 'TRY',
          items: [cashbox],
        });
        return;
      }

      const existing = groupMap.get(key);
      if (existing) {
        existing.totalBalance += cashbox.balance;
        existing.count += 1;
        existing.items.push(cashbox);
      } else {
        const branchName = cashbox.companyBranchName
          || branches.find((b: any) => (b.id || b.branchId) === key)?.name
          || key;

        groupMap.set(key, {
          branchId: key,
          branchName,
          totalBalance: cashbox.balance,
          count: 1,
          currency: cashbox.currency || 'TRY',
          items: [cashbox],
        });
      }
    });

    // Şube grupları + şubesiz bireysel kasalar birleştir, bakiyeye göre sırala
    return [...Array.from(groupMap.values()), ...noBranchItems].sort((a, b) => b.totalBalance - a.totalBalance);
  }, [cashboxes, branches, t]);

  return (
    <StyledCard>
      <CardHeader 
        title={t('dashboard.cashboxesWidgetTitle')}
        titleTypographyProps={{ fontWeight: 'bold' }}
      />
      <Divider />
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : branchGroups.length === 0 ? (
          <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
            {t('dashboard.cashboxesWidgetNoData')}
          </Typography>
        ) : (
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            {branchGroups.map((group, index) => (
              <Box 
                key={group.branchId || `no-branch-${index}`}
                onClick={() => setSelectedGroup(group)}
                sx={{ 
                  p: 1.5, 
                  borderBottom: index < branchGroups.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography 
                    variant="body2" 
                    fontWeight="bold" 
                    sx={{ maxWidth: '55%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {group.branchName}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="bold" 
                    color={group.totalBalance >= 0 ? 'success.main' : 'error.main'}
                  >
                    {formatBalance(group.totalBalance, group.currency)}
                  </Typography>
                </Box>
                {group.branchId && (
                  <Typography variant="caption" color="textSecondary">
                    {t('dashboard.cashboxesWidgetCount', { count: group.count })}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      {/* Şube kasaları detay modalı */}
      <Dialog
        open={!!selectedGroup}
        onClose={() => setSelectedGroup(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalAtmIcon color="success" fontSize="small" />
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              {selectedGroup?.branchName}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setSelectedGroup(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {selectedGroup?.items.map((item, idx) => (
            <Box
              key={item.id || idx}
              sx={{
                px: 2, py: 1.25,
                borderBottom: idx < (selectedGroup?.items.length || 0) - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.name || t('dashboard.cashboxesWidgetDefaultName')}
                  </Typography>
                  {item.description && (
                    <Typography variant="caption" color="textSecondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                      {item.description}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={item.balance >= 0 ? 'success.main' : 'error.main'}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {formatBalance(item.balance, item.currency || 'TRY')}
                </Typography>
              </Box>
            </Box>
          ))}
          {/* Toplam */}
          <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'action.hover' }}>
            <Typography variant="body2" fontWeight="bold">{t('dashboard.total')}</Typography>
            <Typography variant="body2" fontWeight="bold" color={(selectedGroup?.totalBalance || 0) >= 0 ? 'success.main' : 'error.main'}>
              {selectedGroup ? formatBalance(selectedGroup.totalBalance, selectedGroup.currency) : ''}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </StyledCard>
  );
};

export default CashboxListWidget;
